// Pure helpers for the Team Dashboard. Every score comes from the server (live calculation for the
// active period, stored final snapshot for a locked one); nothing is recalculated here and a
// missing value stays null.

export const toNumber = value => (value == null || value === "" || !Number.isFinite(Number(value)) ? null : Number(value));

// One row per member: the server scores and the reviews waiting on the viewer (current period only).
export function memberRows({ members = [], scoresFor = () => null, reviewsWaiting = () => 0, historical = false, positionOf = () => null }) {
    return members
        .map(member => {
            const scores = scoresFor(member.id) || {};
            return {
                id: member.id,
                name: member.name || "—",
                member,
                position: positionOf(member) || null,
                cvi: toNumber(scores.cvi),
                individual: toNumber(scores.individual),
                collective: toNumber(scores.organizational),
                waiting: historical ? null : reviewsWaiting(member.id),
            };
        })
        // Highest CVI first; members without a result last, by name.
        .sort((a, b) => (a.cvi == null) - (b.cvi == null) || (b.cvi ?? 0) - (a.cvi ?? 0) || String(a.name).localeCompare(String(b.name)));
}

// Indicator rows from the engine result plus the approval state of the period's submitted values.
export function indicatorRows({ defs = [], engineFor = () => null, records = [], periodId = null, statusOf }) {
    return defs.map(def => {
        const engine = engineFor(def.id) || null;
        const achievement = toNumber(engine?.achievement);
        const values = records.filter(r => String(r.indicatorId) === String(def.id) && String(r.periodId) === String(periodId));
        const count = status => values.filter(v => status.includes(String(v.status || "").toLowerCase())).length;
        return {
            id: def.id,
            label: def.label || engine?.name || "—",
            unit: def.unit || engine?.unit || "",
            achievement,
            status: statusOf(achievement),
            actual: toNumber(engine?.actual),
            target: toNumber(engine?.target),
            weight: toNumber(engine?.weight),
            approvedCount: engine ? Number(engine.approvedOccurrenceCount) || 0 : count(["approved", "locked"]),
            awaitingCount: count(["submitted", "resubmitted", "pending_review"]),
            revisionCount: count(["revision_requested"]),
            submittedAny: values.some(v => !["draft", "not_started", ""].includes(String(v.status || "").toLowerCase())),
        };
    }).map(row => ({
        ...row,
        // Approved values exist but the result holds no achievement for them (e.g. an imported final
        // snapshot): say the result is not available rather than "no approved data".
        statusKey: row.status === "no_data" && row.approvedCount > 0 ? "no_result" : row.status,
    }));
}

export function indicatorCounts(rows = []) {
    const counts = { on_track: 0, watch: 0, critical: 0, no_data: 0, no_result: 0 };
    rows.forEach(row => { const key = row.statusKey || row.status; counts[key] = (counts[key] || 0) + 1; });
    return counts;
}

// Mean of the available values only; members without a result are reported, never counted as 0.
export function meanOf(values = []) {
    const available = values.map(toNumber).filter(v => v != null);
    return { value: available.length ? available.reduce((s, v) => s + v, 0) / available.length : null, count: available.length, total: values.length };
}

// Follow-up items that exist in the data; empty when there is nothing to act on.
export function attentionItems({ rows = [], indicators = [], reviews = 0, support = 0, historical = false }) {
    const items = [];
    const notAssessed = rows.filter(r => r.cvi == null).length;
    const counts = indicatorCounts(indicators);
    const awaiting = indicators.reduce((s, i) => s + i.awaitingCount, 0);
    if (!historical && reviews > 0) items.push({ key: "reviews", count: reviews, tone: "warning", action: "inbox" });
    if (!historical && support > 0) items.push({ key: "support", count: support, tone: "warning" });
    if (counts.critical > 0) items.push({ key: "below", count: counts.critical, tone: "danger" });
    if (!historical && awaiting > 0) items.push({ key: "pending", count: awaiting, tone: "info" });
    if (counts.no_data > 0) items.push({ key: "noData", count: counts.no_data, tone: "neutral" });
    if (notAssessed > 0) items.push({ key: "notAssessed", count: notAssessed, tone: "neutral" });
    return items;
}

export function formatScore(value, language = "en", digits = 1) {
    const n = toNumber(value);
    if (n == null) return "—";
    return new Intl.NumberFormat(language === "fr" ? "fr-CA" : "en-CA", { minimumFractionDigits: digits, maximumFractionDigits: digits }).format(n);
}

// "90 %" in French and for "%" units, "4.2 /5" or "12 days" otherwise.
export function formatValue(value, unit, language = "en") {
    const n = toNumber(value);
    if (n == null) return "—";
    const text = new Intl.NumberFormat(language === "fr" ? "fr-CA" : "en-CA", { maximumFractionDigits: 1 }).format(n);
    const u = String(unit || "").trim();
    if (!u) return text;
    if (u === "%") return language === "fr" ? `${text}\u00a0%` : `${text}%`;
    return `${text} ${u}`;
}
